// @ts-strict-ignore
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UnitConverter } from '@3d-scene/common/util/unit-converter';
import { CustomArticleInfoVO } from '@project/components/article/model/vo/custom-article.vo';
import { PasteArticlePresenter } from '@project/components/article/presenter/paste-article.presenter';
import { ArticleTypeConstants } from '@article/constants/article-type-constants';
import { ProjectHelper } from '@project/helper/project.helper';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { CopiedRangeOnLineArticleInformation } from '@project/components/article/model/copied-range-on-line-article-information';
import { AlertHelper } from '@shared/alert/alert.helper';
import { ArticleManager } from '@article/manager/article.manager';
import { AlertButton } from '@shared/alert/alertButton';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ValidatorFactory } from '@shared/validator.factory';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { CustomValidators } from '@shared/custom-validators';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ArticleTypeIds } from '@article/constants/article-type-ids';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ProjectModel } from '@project/model/project.model';
import { MountingRailHelper } from '@project/helper/mounting-rail.helper';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { UndoRedoUtils } from '@shared/undo/undo-redo-utils';
import { BlockingZoneArticleManagerFactory } from '../manager/blocking-zone-article-manager.factory';
import { InsertArticleUtils } from '../util/insert-article-utils';
import { ArticleByTypeManager } from '@article/manager/article-by-type.manager';
import { Log } from '@shared/debug/log';
import { Subscription } from 'rxjs';
import { InsertArticleService } from '../service/insert-article.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { SelectionModelService } from '../service/selection-model.service';
import { PasteArticleManager } from '../manager/paste-article.manager';
import { BuildingBlockPasteArticleManager } from '../manager/building-block-paste-article.manager';
import { PasteArticleService } from '../service/paste-article.service';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { LocaleUtils } from '@shared/util/locale-utils';

export interface PasteArticleOperation {
  label: string;
  disabled: boolean;
  action: number;
}

@Component({
  standalone: false,
  selector: 'app-paste-article',
  templateUrl: './paste-article.component.html',
})
export class PasteArticleComponent implements OnInit, OnDestroy {
  public operations: PasteArticleOperation[] = [];

  public isLeadingValue: boolean = false;

  private pasteArticlePresenter: PasteArticlePresenter;

  @Input() configureArticleLength: boolean = true;
  @Input() showRepeatStepper: boolean = false;
  @Input() articleTypeId: number | undefined;

  public configureLeadingMeasurement: boolean = false;

  private _articleRetrievedSubscription: Subscription;
  private _pasteOperationCompletedSubscription: Subscription;

  public form: FormGroup;

  public get isPasteOperation(): boolean {
    return this.pasteArticlePresenter.isPasteOperation;
  }

  public set isPasteOperation(value: boolean) {
    this.pasteArticlePresenter.isPasteOperation = value;
  }

  public get customArticleInfo(): CustomArticleInfoVO {
    return this.pasteArticlePresenter.customArticleInfo;
  }

  public set customArticleInfo(customInfo: CustomArticleInfoVO) {
    this.pasteArticlePresenter.customArticleInfo = customInfo;
  }

  public get isPresenterInitialized(): boolean {
    return this.pasteArticlePresenter.initialized;
  }

  public get isTypeCover(): boolean {
    return this.pasteArticlePresenter.rangeOnLineArticleType === ArticleTypeConstants.TYPE_COVER;
  }

  constructor(
    projectModel: ProjectModel,
    projectArticleService: ProjectArticleService,
    mountingRailHelper: MountingRailHelper,
    buildingBlockPasteArticleManager: BuildingBlockPasteArticleManager,
    selectionModelService: SelectionModelService,
    undoRedoHelper: UndoRedoHelper,
    blockingZoneArticleManagerFactory: BlockingZoneArticleManagerFactory,
    containerService: ContainerService,
    undoRedoUtils: UndoRedoUtils,
    insertArticleHelperFactory: InsertArticleHelperFactory,
    insertArticleUtils: InsertArticleUtils,
    insertArticleService: InsertArticleService,
    applicationRefreshService: ApplicationRefreshService,
    detachArticleUsageService: DetachArticleUsageService,
    private formBuilder: FormBuilder,
    private bsModalRef: BsModalRef,
    private projectHelper: ProjectHelper,
    private alertHelper: AlertHelper,
    private articleManager: ArticleManager,
    private pasteArticleManager: PasteArticleManager,
    private pasteArticleService: PasteArticleService,
    private localeResourceManager: LocaleResourceManager,
    private articleByTypeManager: ArticleByTypeManager,
    private validatorFactory: ValidatorFactory,
  ) {
    this.pasteArticlePresenter = new PasteArticlePresenter(
      insertArticleUtils,
      alertHelper,
      projectModel,
      projectArticleService,
      mountingRailHelper,
      pasteArticleManager,
      buildingBlockPasteArticleManager,
      articleByTypeManager,
      selectionModelService,
      undoRedoHelper,
      blockingZoneArticleManagerFactory,
      containerService,
      undoRedoUtils,
      insertArticleHelperFactory,
      insertArticleService,
      applicationRefreshService,
      detachArticleUsageService
    );
  }

  public ngOnInit(): void {
    if (this.articleTypeId === ArticleTypeIds.BLOCKING_ZONE || this.articleTypeId === ArticleTypeIds.EMPTY_ZONE) {
      this.configureLeadingMeasurement = true;
    }
    this._pasteOperationCompletedSubscription = this.pasteArticleService.pasteOperationCompleted$.subscribe({
      next: (data) => this.pasteOperationCompleted(data),
      error: (error) => Log.error('Error in pasteOperationCompleted subscription: ', error)
    });
    this._articleRetrievedSubscription = this.articleByTypeManager.articlesRetrieved$.subscribe({
      next: (typeId) => this.articleByTypeRetrieved(typeId),
      error: (error) => Log.error('Error in articles retrieved subscription: ', error)
    })
    if (this.articleTypeId) {
      this.pasteArticlePresenter.articleTypeId = this.articleTypeId;
    }
    if (this.customArticleInfo) {
      this.pasteArticlePresenter.customArticleInfo = this.customArticleInfo;
    }
    this.operations = this.getOperations();
    const selectedOperation: number = this.pasteArticlePresenter.articlesOnRailExist
      ? PasteArticlePresenter.PASTE_AFTER_LAST_ARTICLE_ON_RAIL
      : PasteArticlePresenter.PASTE_AT_START;
    this.form = this.formBuilder.group({
      currentPasteOperation: [selectedOperation, Validators.required],
      pasteArticlesLength: [
        '10',
        Validators.compose([
          CustomValidators.trimRequired,
          this.validatorFactory.number({min: 1, max: Math.pow(2, 31) - 1}),
        ]),
      ],
      pasteArticlesDistance: [
        '0',
        Validators.compose([
          CustomValidators.trimRequired,
          this.validatorFactory.number({min: 0, max: Math.pow(2, 31) - 1}),
        ]),
      ],
      repeatStepper: [1],
    });
  }

  public ngOnDestroy(): void {
    this._articleRetrievedSubscription?.unsubscribe();
    this._articleRetrievedSubscription = undefined;

    this._pasteOperationCompletedSubscription?.unsubscribe();
    this._pasteOperationCompletedSubscription = undefined;
  }

  public isMoveArticleAllowed(container: ArticleContainer) {
    return this.projectHelper.isMoveArticleAllowed(container);
  }

  public close(): void {
    this.bsModalRef.hide();
  }

  public async submit(): Promise<void> {
    if (!this.form.valid) {
      return;
    }
    this.repeatStepperCheck();
    let outDatedArticleVO: ArticleVO = null;
    const copiedInfo: CopiedRangeOnLineArticleInformation = this.pasteArticleManager.getRangeOnLineInfo();
    if (copiedInfo) {
      for (const copiedArticleInfo of copiedInfo.articlesInformation) {
        const articleVO: ArticleVO = this.articleManager.getCachedArticle(
          copiedArticleInfo.articleUsage.articleId,
          copiedArticleInfo.articleUsage.articleVersion
        );
        if (ArticleUtils.isArticleOutdated(articleVO)) {
          outDatedArticleVO = articleVO;
        }
      }
    }
    if (outDatedArticleVO) {
      const answer = await this.alertHelper.showAlert('outdatedArticlesAlert.title', ArticleUtils.getOutdatedArticleLabelWithContinue(outDatedArticleVO, this.localeResourceManager), null,
        [AlertButton.YES, AlertButton.NO], "error");

        if (answer === AlertButton.YES) {
          await this.executePasteOperation();
        }
    } else {
      await this.executePasteOperation();
    }
  }

  public checkControlValidity(controlName: string): boolean {
    const control = this.form.controls[controlName];
    return control.invalid && control.touched;
  }

  public repeatStepperCheck(): void {
    if (!this.form.controls['repeatStepper'].value) {
      this.form.controls['repeatStepper'].setValue(1);
    }
  }

  private async executePasteOperation(): Promise<void> {
    const offsetValue: number = UnitConverter.fromTo('mm', '3d', this.localeResourceManager.parseNumber(this.form.controls.pasteArticlesDistance.value));
    let lengthValue: number;
    if (this.configureArticleLength) {
      lengthValue = UnitConverter.fromTo('mm', '3d', this.localeResourceManager.parseNumber(this.form.controls.pasteArticlesLength.value));
    }

    let isLeadingValue: boolean = false;
    if (this.configureLeadingMeasurement) {
      isLeadingValue = this.isLeadingValue;
    }

    const repeatCount: number = this.form.controls['repeatStepper'].value;
    await this.pasteArticlePresenter.executePasteOperation(this.form.controls.currentPasteOperation.value, offsetValue, lengthValue,
      isLeadingValue, repeatCount ? repeatCount : 1);
    if (!this.isPasteOperation) {
      this.close();
    }
  }

  private getOperations(): PasteArticleOperation[] {
    return [
      {
        label: this.getPasteAfterLastArticleRadioButtonLabel(this.pasteArticlePresenter.rangeOnLineArticleType),
        disabled: !this.pasteArticlePresenter.articlesOnRailExist,
        action: PasteArticlePresenter.PASTE_AFTER_LAST_ARTICLE_ON_RAIL,
      },
      {
        label: this.getPasteAfterEachSelectedArticleRadioButtonLabel(this.pasteArticlePresenter.rangeOnLineArticleType),
        disabled: !this.pasteArticlePresenter.selectedArticlesOnRailExist,
        action: PasteArticlePresenter.PASTE_AFTER_EACH_SELECTED_ARTICLE,
      },
      {
        label: this.getPasteAfterLastBeforeSelectedArticleRadioButtonLabel(this.pasteArticlePresenter.rangeOnLineArticleType),
        disabled: !this.pasteArticlePresenter.lastArticleBeforeSelectionExists,
        action: PasteArticlePresenter.PASTE_AFTER_LAST_ARTICLE_BEFORE_SELECTION,
      },
      {
        label: this.getPasteAtStartRadioButtonLabel(this.pasteArticlePresenter.rangeOnLineArticleType),
        disabled: false,
        action: PasteArticlePresenter.PASTE_AT_START,
      },
    ];
  }

  private getPasteAtStartRadioButtonLabel(articleType: string): string {
    let result: string;
    switch (articleType) {
      case ArticleTypeConstants.TYPE_N_BUSBAR:
        result = 'pasteArticlePopup.label.paste_at_start_on_busbar';
        break;
      case ArticleTypeConstants.TYPE_COVER:
        result = 'pasteArticlePopup.label.paste_at_start_on_cover';
        break;
      default:
        result = 'pasteArticlePopup.label.paste_at_start_on_rail';
        break;
    }
    return result;
  }

  private getPasteAfterLastArticleRadioButtonLabel(articleType: string): string {
    let result: string;
    switch (articleType) {
      case ArticleTypeConstants.TYPE_N_BUSBAR:
        result = 'pasteArticlePopup.label.paste_after_last_on_busbar';
        break;
      case ArticleTypeConstants.TYPE_COVER:
        result = 'pasteArticlePopup.label.paste_after_last_on_cover';
        break;
      default:
        result = 'pasteArticlePopup.label.paste_after_last_on_rail';
        break;
    }
    return result;
  }

  private getPasteAfterEachSelectedArticleRadioButtonLabel(articleType: string): string {
    let result: string;
    switch (articleType) {
      case ArticleTypeConstants.TYPE_N_BUSBAR:
        result = 'pasteArticlePopup.label.paste_after_each_selected_on_busbar';
        break;
      case ArticleTypeConstants.TYPE_COVER:
        result = 'pasteArticlePopup.label.paste_after_each_selected_on_cover';
        break;
      default:
        result = 'pasteArticlePopup.label.paste_after_each_selected_on_rail';
        break;
    }
    return result;
  }

  private getPasteAfterLastBeforeSelectedArticleRadioButtonLabel(articleType: string): string {
    let result: string;
    switch (articleType) {
      case ArticleTypeConstants.TYPE_N_BUSBAR:
        result = 'pasteArticlePopup.label.paste_after_last_before_selected_on_busbar';
        break;
      case ArticleTypeConstants.TYPE_COVER:
        result = 'pasteArticlePopup.label.paste_after_last_before_selected_on_cover';
        break;
      default:
        result = 'pasteArticlePopup.label.paste_after_last_before_selected_on_rail';
        break;
    }
    return result;
  }

  private async pasteOperationCompleted(data): Promise<void> {
    await this.pasteArticlePresenter.pasteOperationCompletedHandler(data);
    this.close();
  }

  public articleByTypeRetrieved(articleTypeId: number): void {
    if (this.pasteArticlePresenter.articleToInsert || this.pasteArticlePresenter.articleTypeId !== articleTypeId) {
      return;
    }
    this.pasteArticlePresenter.articleToInsert = this.articleByTypeManager.getArticleHeadByType(articleTypeId);
    if (!this.pasteArticlePresenter.articleToInsert) {
      this.displayArticleToInsertNotFoundMessage(articleTypeId);
    }
  }

  private async displayArticleToInsertNotFoundMessage(articleTypeId: number): Promise<void> {
    await this.alertHelper.showError(
      'pasteArticlePopup.error.title.article_to_insert_not_found',
      'pasteArticlePopup.error.message.article_to_insert_not_found',
      {translationParams: {0: this.getLocalizedArticleType(articleTypeId)}});
  }

  private getLocalizedArticleType(articleTypeId: number): string {
    return LocaleUtils.getLocalizedArticleType(this.localeResourceManager, articleTypeId);
  }
}
